// Generic free-text multi-value input. Enter / comma / blur convert the
// pending draft into a chip; Backspace in an empty input removes the
// last chip; exact duplicates are rejected silently. Whitespace is
// trimmed and pure-whitespace drafts ignored. Click ✕ on any chip to
// remove it. The placeholder hides once at least one chip is present so
// the input doesn't double-up the helper guidance.

import { clsx } from "clsx";
import { useState, type ChangeEvent, type KeyboardEvent } from "react";
import { X } from "lucide-react";

export interface ChipInputProps {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  /** Optional accessible label for the inner text input. When omitted,
   *  falls back to the placeholder so consumers in multi-input forms
   *  can still scope `getByRole("textbox", { name })` to this primitive
   *  without coupling the component to a specific label string. */
  inputAriaLabel?: string;
}

export default function ChipInput({
  values,
  onChange,
  placeholder,
  helperText,
  disabled,
  inputAriaLabel,
}: ChipInputProps) {
  const [draft, setDraft] = useState("");

  const commit = (raw: string): boolean => {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return false;
    if (values.includes(trimmed)) return false;
    onChange([...values, trimmed]);
    return true;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (commit(draft)) setDraft("");
      return;
    }
    if (e.key === "Backspace" && draft.length === 0 && values.length > 0) {
      e.preventDefault();
      onChange(values.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (commit(draft)) setDraft("");
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
  };

  const removeChip = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div
        className={clsx(
          "input h-auto min-h-[2.5rem] flex flex-wrap items-center gap-1.5 py-1.5",
          "focus-within:ring-2 focus-within:ring-brand-100 dark:focus-within:ring-brand-900/40",
          disabled && "opacity-60",
        )}
      >
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-caption text-slate-700 dark:bg-slate-700 dark:text-slate-200"
          >
            {v}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${v}`}
                className="-mr-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-600 dark:hover:text-slate-100"
                onClick={() => removeChip(i)}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        <input
          type="text"
          value={draft}
          placeholder={values.length === 0 ? placeholder : ""}
          aria-label={inputAriaLabel ?? placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          className="flex-1 min-w-[6rem] border-0 bg-transparent p-0 text-body text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-0 dark:text-slate-100 dark:placeholder:text-slate-500"
        />
      </div>
      {helperText && (
        <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
          {helperText}
        </p>
      )}
    </div>
  );
}
